import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loading } from '@/components/ui/loading'
import { PermissionGuard } from '@/components/ui/PermissionGuard'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '@/lib/utils'
import { Link } from 'react-router-dom'
import { ExternalLabSummary } from '@/components/externalLabs/ExternalLabSummary'
import {
    FileText,
    Users,
    TrendingUp,
    Clock,
    Activity,
    CheckCircle,
    AlertCircle,
    Plus
} from 'lucide-react'
import {Id} from "../../convex/_generated/dataModel";
import { getRoleLabel } from '../../convex/lib/permissions'
import { prescriptionDisplayNumber } from '../../convex/lib/prescriptionNumber'

function DashboardContent() {
    const { profile } = useAuth()
    const { hasPermission, canCreatePrescriptions } = usePermissions()

    // Get dashboard statistics - only if user has proper setup
    const canViewAllPrescriptions = hasPermission('view_all_prescriptions')
    const canViewStats = profile && hasPermission('view_prescriptions') && (
        canViewAllPrescriptions ||
        (profile.company_id && hasPermission('view_company_data'))
    )

    const stats = useQuery(
        api.support.getDashboardStats,
        canViewStats ? {
            // Il laboratorio vede il totale globale; gli studi soltanto la propria azienda.
            ...(!canViewAllPrescriptions && profile.company_id ? { company_id: profile.company_id } : {})
        } : "skip"
    )

    // Get recent prescriptions
    const recentPrescriptions = useQuery(
        api.prescriptions.listPrescriptions,
        profile && hasPermission('view_prescriptions') ? {
            paginationOpts: { numItems: 5, cursor: null },
            // Il laboratorio non necessita company_id, gli studi usano la propria.
            ...(!canViewAllPrescriptions && profile.company_id ? { company_id: profile.company_id } : {})
        } : "skip"
    )
    const company = useQuery(
        api.companies.getCompany,
        profile && !canViewAllPrescriptions && profile.company_id
            ? { companyId: profile.company_id as Id<"companies"> }
            : "skip"
    )

    // Stato tecnico visibile secondo la policy configurata per ruolo.
    const systemHealth = useQuery(
        api.support.getSystemHealth,
        hasPermission('view_system_health') ? {} : "skip"
    )

    if (!profile) {
        return <Loading text="Caricamento dashboard..." />
    }

    return (
        <div className="space-y-6">
            {/* New User Notice */}
            {profile.ruolo === 'staff' && profile.company_id && company?.nome === 'zz_prova' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                            <h3 className="font-medium text-blue-900">Account in configurazione</h3>
                            <p className="text-blue-700 text-sm mt-1">
                                Il tuo account è stato creato con successo. Un amministratore deve ancora assegnarti
                                il ruolo corretto e l'azienda di appartenenza. Nel frattempo, alcune funzionalità
                                potrebbero non essere disponibili.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                            Benvenuto, {profile.nome} {profile.cognome}
                        </h1>
                        <p className="text-gray-600 mt-1 text-sm sm:text-base">
                            {getRoleLabel(profile.ruolo)}
                            {profile.company_id && ' • ' + (company?.nome || 'Azienda')}
                        </p>
                    </div>

                    {(canCreatePrescriptions || hasPermission('create_external_prescription')) && (
                        <Link to="/prescriptions/new">
                            <Button className="bg-primary hover:bg-primary/90 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm w-full sm:w-auto">
                                <Plus className="w-5 h-5 mr-2" />
                                Nuova Prescrizione
                            </Button>
                        </Link>
                    )}
                </div>
            </div>

            {profile.company_id && ['clinic_owner', 'staff', 'amministratore'].includes(profile.ruolo) && hasPermission('view_external_prescriptions') && (
                <ErrorBoundary><ExternalLabSummary key={profile.company_id} /></ErrorBoundary>
            )}

            {/* Statistics Cards */}
            {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-primary/5 to-primary/10">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-gray-700">
                                Prescrizioni PrimoLab
                            </CardTitle>
                            <div className="p-2 bg-primary/10 rounded-lg">
                                <FileText className="h-4 w-4 text-primary" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalPrescriptions}</div>
                            <p className="text-xs text-gray-600 mt-1">
                                {stats.recentPrescriptions} negli ultimi 30 giorni
                            </p>
                        </CardContent>
                    </Card>

                    {hasPermission('view_prices') && (
                        <Card className="border-0 shadow-sm bg-gradient-to-br from-secondary/5 to-secondary/10">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium text-gray-700">
                                    Totale PrimoLab
                                </CardTitle>
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <TrendingUp className="h-4 w-4 text-secondary" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue ?? 0)}</div>
                                <p className="text-xs text-gray-600 mt-1">
                                    Media: {formatCurrency(stats.averageOrderValue ?? 0)}
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {hasPermission('view_company_data') && (
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">
                                    Utenti Attivi
                                </CardTitle>
                                <Users className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{stats.userCount}</div>
                                <p className="text-xs text-muted-foreground">
                                    Nella tua organizzazione
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                In Lavorazione
                            </CardTitle>
                            <Clock className="h-4 w-4 text-muted-foreground" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {stats.statusCounts.in_lavorazione || 0}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Prescrizioni attive
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Status Overview */}
            {stats && (
                <Card>
                    <CardHeader>
                        <CardTitle>Stato prescrizioni PrimoLab</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {Object.entries(stats.statusCounts).map(([status, count]) => (
                                <div key={status} className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">{count as number}</div>
                                    <Badge
                                        variant="outline"
                                        className={getStatusColor(status)}
                                    >
                                        {getStatusLabel(status)}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Recent Prescriptions */}
            {hasPermission('view_prescriptions') && <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Prescrizioni PrimoLab recenti</CardTitle>
                    <Link to="/prescriptions">
                        <Button variant="outline" size="sm">
                            Vedi Tutte
                        </Button>
                    </Link>
                </CardHeader>
                <CardContent>
                    {recentPrescriptions ? (
                        recentPrescriptions.page.length > 0 ? (
                            <div className="space-y-4">
                                {recentPrescriptions.page.map((prescription: any) => (
                                    <div
                                        key={prescription._id}
                                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3">
                                                <div className="font-medium">
                                                    {prescriptionDisplayNumber(prescription)}
                                                </div>
                                                <Badge className={getStatusColor(prescription.stato)}>
                                                    {getStatusLabel(prescription.stato)}
                                                </Badge>
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">
                                                {prescription.NomePaziente} • {formatDate(prescription.created_at)}
                                            </div>
                                        </div>

                                        <div className="text-right">
                                            {hasPermission('view_prices') && (
                                                <div className="font-medium">
                                                    {formatCurrency(prescription.PrezzoNetto)}
                                                </div>
                                            )}
                                            <Link
                                                to={`/prescriptions/${prescription._id}`}
                                                className="text-sm text-primoup-600 hover:text-primoup-700"
                                            >
                                                Dettagli →
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Nessuna prescrizione trovata</p>
                                {canCreatePrescriptions && (
                                    <Link to="/prescriptions/new" className="mt-2 inline-block">
                                        <Button variant="outline">
                                            Crea la prima prescrizione
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        )
                    ) : (
                        <Loading text="Caricamento prescrizioni..." />
                    )}
                </CardContent>
            </Card>}

            {/* System Health (Admin Only) */}
            {systemHealth && hasPermission('view_system_health') && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center space-x-2">
                            <Activity className="w-5 h-5" />
                            <span>Stato Sistema</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    {systemHealth.apiHealth.successRate.toFixed(1)}%
                                </div>
                                <div className="text-sm text-gray-600">API Success Rate</div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-600">
                                    {systemHealth.primoUpConnections}
                                </div>
                                <div className="text-sm text-gray-600">PrimoUp Connessioni</div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-purple-600">
                                    {systemHealth.activeUsers}
                                </div>
                                <div className="text-sm text-gray-600">Utenti Attivi</div>
                            </div>

                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-600">
                                    {systemHealth.activeCompanies}
                                </div>
                                <div className="text-sm text-gray-600">Aziende Attive</div>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                            <span>Ultimo aggiornamento: {formatDate(systemHealth.lastUpdated)}</span>
                            <div className="flex items-center space-x-2">
                                {systemHealth.apiHealth.successRate > 95 ? (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                                )}
                                <span>Sistema Operativo</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}

export function Dashboard() {
    return (
        <ErrorBoundary>
            <PermissionGuard>
                <DashboardContent />
            </PermissionGuard>
        </ErrorBoundary>
    )
}
